// Building-block helpers for types that need payload authorship (signed
// operations). Analogous to how refs.ts in MVT provides helpers for
// inter-object references.
//
// Public keys are NOT embedded in every payload. They are stored once by
// the type (e.g. in a creation payload or identity registry) and looked
// up by KeyId during verification. This keeps authored payloads small,
// which matters especially for post-quantum signing suites.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { base64, getSigningSuite, serializePublicKey, deserializePublicKey, keyIdFromPublicKey, stringToUint8Array } from "@hyper-hyper-space/hhs3_crypto";
import { json } from "@hyper-hyper-space/hhs3_json";
export const MAX_KEY_ID_LENGTH = 256;
export const MAX_SIGNATURE_LENGTH = 8192;
export const authoredFormat = {
    author: [json.Type.BoundedString, MAX_KEY_ID_LENGTH],
    signature: [json.Type.BoundedString, MAX_SIGNATURE_LENGTH],
};
export function isAuthoredPayload(payload) {
    return typeof payload === 'object'
        && !Array.isArray(payload)
        && typeof payload['author'] === 'string'
        && typeof payload['signature'] === 'string';
}
export function extractAuthor(payload) {
    if (typeof payload === 'object' && !Array.isArray(payload) && typeof payload['author'] === 'string') {
        return payload['author'];
    }
    return undefined;
}
function canonicalBytesWithoutSignature(payload) {
    const stripped = {};
    for (const key of Object.keys(payload)) {
        if (key !== 'signature') {
            stripped[key] = payload[key];
        }
    }
    return stringToUint8Array(json.toStringNormalized(stripped));
}
export function serializePublicKeyToBase64(pk) {
    const bytes = serializePublicKey(pk);
    return base64.fromArrayBuffer(bytes.slice().buffer);
}
export function deserializePublicKeyFromBase64(b64) {
    const buf = base64.toArrayBuffer(b64);
    return deserializePublicKey(new Uint8Array(buf));
}
export function computeKeyId(pk, hashSuite) {
    return keyIdFromPublicKey(pk, hashSuite);
}
export function signPayload(payload, author) {
    return __awaiter(this, void 0, void 0, function* () {
        const suite = getSigningSuite(author.publicKey.suite);
        if (suite === undefined) {
            throw new Error(`Signing suite '${author.publicKey.suite}' not registered`);
        }
        const withAuthor = Object.assign(Object.assign({}, payload), { author: author.keyId, signature: '' });
        const message = canonicalBytesWithoutSignature(withAuthor);
        const sigBytes = yield suite.sign(message, author.secretKey);
        const signature = base64.fromArrayBuffer(sigBytes.slice().buffer);
        return Object.assign(Object.assign({}, withAuthor), { signature });
    });
}
export function verifyPayloadSignature(payload, keyLookup) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!isAuthoredPayload(payload)) {
            return false;
        }
        const pk = yield keyLookup(payload.author);
        if (pk === undefined) {
            return false;
        }
        const suite = getSigningSuite(pk.suite);
        if (suite === undefined) {
            return false;
        }
        const message = canonicalBytesWithoutSignature(payload);
        const sigBuf = base64.toArrayBuffer(payload.signature);
        const sigBytes = new Uint8Array(sigBuf);
        return suite.verify(message, sigBytes, pk.key);
    });
}
